import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, ShieldCheck, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { User } from '@/api/types/auth.types';

export function PortalMfaSetupPage() {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const { data: setup, isLoading, isError } = useQuery({
    queryKey: ['portal-mfa-setup'],
    queryFn: async () => {
      const { data } = await apiClient.get('/portal/auth/mfa/setup');
      return data.data as { qrUrl: string; secret: string };
    },
    retry: false,
    staleTime: Infinity,
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/portal/auth/mfa/verify', {
        totp_code: code.replace(/\s/g, ''),
      });
      if (!data.data) throw new Error(data.errors?.[0]?.message ?? 'Verification failed');
      return data.data as { sessionToken: string; user: Record<string, unknown> };
    },
    onSuccess: (data) => {
      login(data.sessionToken, data.user as unknown as User);
      toast.success('Two-factor authentication enabled');
      navigate('/portal/dashboard');
    },
    onError: () => toast.error('Invalid code — please try again'),
  });

  function handleCopy() {
    if (!setup?.secret) return;
    navigator.clipboard.writeText(setup.secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (code.replace(/\s/g, '').length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }
    verifyMutation.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-10 w-auto" />
          <div className="text-center">
            <h1 className="text-xl font-bold text-text-primary">Set Up Two-Factor Auth</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Required to access the portal</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-text-secondary">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Generating setup code…</span>
            </div>
          ) : isError ? (
            <div className="py-6 text-center space-y-2">
              <p className="text-sm font-medium text-accent-rose">Failed to load MFA setup.</p>
              <p className="text-xs text-text-secondary">Please refresh the page or sign in again.</p>
            </div>
          ) : (
            <>
              <div className="mb-5 flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent-cyan" />
                <div>
                  <h2 className="text-base font-semibold text-text-primary">Scan with your authenticator</h2>
                  <p className="mt-0.5 text-sm text-text-secondary">
                    Use Google Authenticator, Authy, or any TOTP-compatible app.
                  </p>
                </div>
              </div>

              {/* QR Code */}
              <div className="mb-4 flex justify-center">
                <div className="rounded-lg border border-border-subtle bg-white p-2">
                  <img
                    src={setup?.qrUrl}
                    alt="MFA QR Code"
                    className="h-40 w-40"
                  />
                </div>
              </div>

              {/* Manual entry secret */}
              <div className="mb-5 space-y-1.5">
                <p className="text-xs text-text-secondary">Can't scan? Enter this key manually:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded border border-border-subtle bg-bg-surface-2 px-3 py-1.5 text-xs font-mono text-text-primary break-all select-all">
                    {setup?.secret}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="shrink-0 rounded border border-border-subtle bg-bg-surface-2 p-1.5 text-text-secondary hover:text-text-primary"
                    aria-label="Copy secret"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-accent-emerald" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="mfa-setup-code">Verification code</Label>
                  <Input
                    id="mfa-setup-code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000 000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, ''))}
                    maxLength={7}
                    autoFocus
                    autoComplete="one-time-code"
                    className="text-center tracking-widest text-lg font-mono"
                  />
                  <p className="text-xs text-text-secondary">
                    Enter the 6-digit code shown in your app to confirm setup.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={verifyMutation.isPending || code.replace(/\s/g, '').length < 6}
                >
                  {verifyMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying…</>
                  ) : (
                    'Enable Two-Factor Auth'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
